import { API_BASE_URL } from "@appretio/api";
import { useAuth0 } from "@appretio/auth";
import {
  Button,
  Divider,
  InputField,
  Label,
  Option,
  ProgressSpinner,
  SingleSelect
} from "@appretio/ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getFieldLabel } from "../../forecastContext";
import { useContextFilters } from "../../hooks/manageCubes/useContextFilters";
import { useCubeContext } from "../../hooks/manageCubes/useCubeContext";
import { useExportCube } from "../../hooks/manageCubes/useExportCube";
import { useExportList } from "../../hooks/manageCubes/useExportList";
import { useManageCubesOperationToast } from "../../hooks/manageCubes/useManageCubesOperationToast";
import { usePollExport } from "../../hooks/manageCubes/usePollExport";
import { ConfirmOperationDialog } from "./ConfirmOperationDialog";
import { CubeContextFilterGrid } from "./CubeContextFilterGrid";
import {
  buttonRow,
  fieldGroup,
  formColumn,
  hintText,
  narrowField,
  pollingContainer
} from "./manageCubesOperations.css";

/** Props for the Export Cube operation form. */
interface ExportOperationProps {
  /** The cube ID to export data from. */
  cubeId: string;
}

function getDefaultLabel(): string {
  return `appretio_cube_${new Date().toISOString().slice(0, 19).replace(/[T:]/g, "_")}`;
}

export function ExportOperation({ cubeId }: ExportOperationProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [label, setLabel] = useState(getDefaultLabel);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportState, setExportState] = useState<{
    uid: string;
    sfqid: string;
  } | null>(null);
  const { showSuccess, ToastElement } = useManageCubesOperationToast();
  const {
    selections: contextSelections,
    activeFilters,
    handleFieldChange: handleContextChange
  } = useContextFilters();

  const { data: cubeContext, isLoading: contextLoading } =
    useCubeContext(cubeId);

  const { data: filteredContext } = useCubeContext(cubeId, activeFilters);

  const exportCube = useExportCube();
  const { data: exportListData, refetch: refetchExports } =
    useExportList(cubeId);

  const isPolling = exportState !== null;
  const { data: pollData } = usePollExport(
    exportState?.uid,
    exportState?.sfqid,
    isPolling
  );

  const exportOptions: Option[] = useMemo(() => {
    if (!exportListData?.exports) return [];
    return exportListData.exports.map((e) => ({
      label: `${e.label} (${e.created_timestamp})`,
      value: e.export_id
    }));
  }, [exportListData]);

  // TODO: Passing the JWT as a query parameter exposes it in server logs,
  // browser history, and Referer headers. Consider replacing with a
  // short-lived single-use download token issued via an authenticated POST.
  const triggerDownload = useCallback(
    async (uid: string) => {
      const token = await getAccessTokenSilently();
      const base = API_BASE_URL.replace(/\/+$/, "");
      const url = `${base}/v2/forecast/exports/download/${uid}?query_token=${encodeURIComponent(token)}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    [getAccessTokenSilently]
  );

  // Auto-download when export is ready
  useEffect(() => {
    if (pollData?.status === "ready" && exportState) {
      triggerDownload(exportState.uid);
      setExportState(null);
      refetchExports();
      showSuccess("Export ready — download started.");
      setLabel(getDefaultLabel());
    }
  }, [pollData, exportState, refetchExports, triggerDownload, showSuccess]);

  function handleConfirm() {
    const context =
      Object.keys(contextSelections).length > 0 ? contextSelections : undefined;

    exportCube.mutate(
      {
        cube_id: cubeId,
        context: context as Record<string, string[]> | undefined,
        label
      },
      {
        onSuccess: (data) => {
          setExportState({ uid: data.uid, sfqid: data.sfqid });
          setDialogOpen(false);
        },
        // The global mutation handler already toasts the failure with the
        // server's reason — a local toast would duplicate it.
        onError: () => {
          setDialogOpen(false);
        }
      }
    );
  }

  function handleDownloadExisting(uid: string) {
    if (uid) triggerDownload(uid);
  }

  return (
    <>
      {ToastElement}
      <div className={formColumn}>
        {isPolling ? (
          <div className={pollingContainer}>
            <ProgressSpinner />
            <p>Export in progress — staging data...</p>
          </div>
        ) : (
          <>
            <p className={hintText}>
              Create a <em>new</em> export. Data is written as Parquet files
              (zipped), covering three financial years (prior, current, next).
            </p>
            <div className={`${fieldGroup} ${narrowField}`}>
              <Label label="Export label" />
              <InputField
                placeholder="Export label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <p className={hintText}>
              Optionally filter which part of the cube to include.
            </p>
            <CubeContextFilterGrid
              options={cubeContext?.options}
              filteredOptions={filteredContext?.options}
              selections={contextSelections}
              hasActiveFilters={!!activeFilters}
              isLoading={contextLoading}
              setSelected={handleContextChange}
            />

            <div className={buttonRow}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setDialogOpen(true)}
                disabled={!label.trim()}
              >
                Export
              </Button>
            </div>

            {exportOptions.length > 0 && (
              <>
                <Divider />
                <p className={hintText}>
                  Select a <em>previous</em> export by label to trigger a
                  re-download.
                </p>
                <div className={narrowField}>
                  <SingleSelect
                    label="Previous export"
                    options={exportOptions}
                    selectedOption={undefined}
                    onChange={handleDownloadExisting}
                    clearable={false}
                    size="md"
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
      <ConfirmOperationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Export Cube"
        confirmText="Export"
        onConfirm={handleConfirm}
        isLoading={exportCube.isPending}
      >
        <p>
          Please review the export. Staging may take several minutes depending
          on how much data is included.
        </p>
        {Object.entries(contextSelections).some(
          ([, vals]) => vals.length > 0
        ) && (
          <>
            <p>
              <strong>Context filter:</strong>
            </p>
            <ul>
              {Object.entries(contextSelections)
                .filter(([, vals]) => vals.length > 0)
                .map(([key, vals]) => (
                  <li key={key}>
                    <strong>{getFieldLabel(key)}:</strong> {vals.join(", ")}
                  </li>
                ))}
            </ul>
          </>
        )}
        {label && (
          <p>
            <strong>Label:</strong> {label}
          </p>
        )}
      </ConfirmOperationDialog>
    </>
  );
}
